import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { matchRoutes, useLocation } from "react-router";
import { Box } from "@mui/material";
import { LoadingButton } from "@components";
import { useGetAndValidateBookings } from "@features/fileDrop/hooks/useValidateBookings";
import { ActivityWithBooking } from "@features/fileDrop/helpers/bookingLocator";
import { UploadedFile } from "@features/fileDrop/components/UploadedFile";
import { usePersistValidBookings } from "../hooks/usePersistValidBookings";
import { ParsingResultActivities } from "./ParsingResultActivities";
import { FileDrop, Props as FileDropProps } from "./FileDrop";
import { UniqueParsingResult } from "./ParsingResult";

const SpacerContainer = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export interface FileDropHandlerProps {
  accountId: number;
  fileDropComponent?: React.FC<FileDropProps>;
}

export const FileDropHandler = ({
  accountId,
  fileDropComponent: FileDropComponent = FileDrop,
}: FileDropHandlerProps) => {
  const [removeFailedActivitiesStarted, setRemoveFailedActivitiesStarted] =
    useState(false);
  const [isParsingFinished, setIsParsingFinished] = useState(false);
  const isDepot = matchRoutes([{ path: "/depots/*" }], useLocation());

  const { t } = useTranslation();
  const [parsingResults, setParsingResults] = useState<UniqueParsingResult[]>(
    []
  );

  const handleParsingResult = (results: UniqueParsingResult[]) => {
    setParsingResults((current) => [...current, ...results]);
  };

  const { data: getAndValidateBookingsResult, isFetching: validatingData } =
    useGetAndValidateBookings(accountId, parsingResults, {
      placeholderData: keepPreviousData,
      enabled: isParsingFinished,
    });

  const { persistValidBookings, isPending: persistingBookings } =
    usePersistValidBookings(isDepot ? `/depots/${accountId}` : "/investments");

  const handlePersistButtonClick = () => {
    persistValidBookings(
      accountId,
      createOrUpdateInvestmentParamsList || [],
      transactionValidationErrorList || []
    );
  };

  const activitiesWithBookings = useMemo(
    () => getAndValidateBookingsResult?.activitiesWithBookings || [],
    [getAndValidateBookingsResult]
  );
  const createOrUpdateInvestmentParamsList =
    getAndValidateBookingsResult?.createOrUpdateInvestmentParamsList;
  const transactionValidationErrorList =
    getAndValidateBookingsResult?.transactionValidationErrorList
      .transactionValidationErrors;

  const handleRemoveFailedActivities = useCallback(() => {
    setParsingResults((current) =>
      current.reduce((acc, parsingResult) => {
        const found = activitiesWithBookings.filter(
          (item) => item.activity.sourceFilename === parsingResult.id
        );

        const activitiesWithoutErrors = found.filter(
          (item) =>
            !transactionValidationErrorList?.some(
              (error) => item.hash === error.hash
            )
        );
        if (!activitiesWithoutErrors || activitiesWithoutErrors.length === 0) {
          return acc;
        }

        const newActivities = parsingResult.activities?.filter((item) =>
          activitiesWithoutErrors.some(
            (activityWithBooking) =>
              activityWithBooking.id === item.parsingResultId
          )
        );

        return [
          ...acc,
          {
            ...parsingResult,
            activities: newActivities,
          },
        ] as UniqueParsingResult[];
      }, [] as UniqueParsingResult[])
    );
  }, [activitiesWithBookings, transactionValidationErrorList]);

  const handleRemoveParsingResult = (parsingResult: UniqueParsingResult) => {
    setParsingResults((current) =>
      current.filter((result) => result.id !== parsingResult.id)
    );
  };

  const handleRemoveActivity = (activity: ActivityWithBooking) => {
    setParsingResults((current) =>
      current.reduce((acc, parsingResult) => {
        if (parsingResult.id === activity.activity.sourceFilename) {
          const newActivities = parsingResult.activities?.filter(
            (item) => item.uuid !== activity.activity.uuid
          );
          if (!newActivities || newActivities.length === 0) {
            return acc;
          }

          return [
            ...acc,
            {
              ...parsingResult,
              activities: newActivities,
            },
          ];
        }
        return [...acc, parsingResult];
      }, [] as UniqueParsingResult[])
    );
  };

  useEffect(() => {
    if (
      removeFailedActivitiesStarted &&
      transactionValidationErrorList?.length
    ) {
      handleRemoveFailedActivities();
    } else {
      setRemoveFailedActivitiesStarted(false);
    }
  }, [
    removeFailedActivitiesStarted,
    transactionValidationErrorList,
    handleRemoveFailedActivities,
  ]);

  return (
    <div data-testid="file-drop-handler">
      <SpacerContainer>
        <FileDropComponent
          onParsingResult={handleParsingResult}
          setIsParsingFinished={setIsParsingFinished}
          isValidating={validatingData}
        />
      </SpacerContainer>

      {parsingResults.map((parsingResult, ix) => {
        const found = activitiesWithBookings.filter(
          (item) => item.activity.sourceFilename === parsingResult.id
        );
        if (!found.length) return null;

        return (
          <SpacerContainer key={`parsing-result-activities-container-${ix}`}>
            <ParsingResultActivities
              key={`parsingResult-activities-${ix}`}
              testid={`parsingResult-activities-${ix}`}
              activitiesWithBooking={found}
              transactionValidationErrors={
                transactionValidationErrorList?.filter((error) =>
                  found.some((activity) => activity.hash === error.hash)
                ) || []
              }
              onRemove={handleRemoveActivity}
            />
          </SpacerContainer>
        );
      })}

      {activitiesWithBookings.length > 0 && (
        <SpacerContainer>
          <Box display="flex" gap={3}>
            {Boolean(transactionValidationErrorList?.length) && (
              <LoadingButton
                variant="secondary"
                disabled={removeFailedActivitiesStarted}
                isFetching={removeFailedActivitiesStarted}
                onClick={() => setRemoveFailedActivitiesStarted(true)}
              >
                {t("transactionUpload.ignoreErrors")}
              </LoadingButton>
            )}
            <LoadingButton
              variant="primary"
              onClick={() => handlePersistButtonClick()}
              disabled={
                !isParsingFinished ||
                persistingBookings ||
                validatingData ||
                (transactionValidationErrorList &&
                  transactionValidationErrorList.length > 0)
              }
              isFetching={persistingBookings || validatingData}
            >
              {t("transactionUpload.persistData")}
            </LoadingButton>
          </Box>
        </SpacerContainer>
      )}

      <SpacerContainer>
        {parsingResults.map((parsingResult, ix) => {
          return (
            <UploadedFile
              parsingResult={parsingResult}
              onRemove={handleRemoveParsingResult}
              key={`parsingResult-${ix}`}
              testid={`parsingResult-${
                parsingResult.successful ? "success" : "fail"
              }-${ix}`}
            />
          );
        })}
      </SpacerContainer>
    </div>
  );
};
